/**
 * Campaign Type Badge Component
 * Displays ONE_TIME, RECURRING, or AB_TEST with appropriate styling
 */

import type { CampaignType } from '../types'

interface CampaignTypeBadgeProps {
  type: CampaignType | string | undefined | null
  iterationNumber?: number | null
  abTestPhase?: string | null
}

const typeConfig: Record<string, { label: string; className: string; icon: string }> = {
  ONE_TIME: {
    label: 'One-Time',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    icon: '📤',
  },
  RECURRING: {
    label: 'Recurring',
    className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    icon: '🔄',
  },
  AB_TEST: {
    label: 'A/B Test',
    className: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    icon: '🧪',
  },
}

export function CampaignTypeBadge({ type, iterationNumber, abTestPhase }: CampaignTypeBadgeProps) {
  const config = typeConfig[type || ''] || {
    label: type || 'Unknown',
    className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    icon: '📋',
  }

  // Build additional info
  let additionalInfo = ''
  if (type === 'RECURRING' && iterationNumber) {
    additionalInfo = ` #${iterationNumber}`
  } else if (type === 'AB_TEST' && abTestPhase) {
    const phaseLabel = abTestPhase === 'PHASE_1' ? 'P1' : abTestPhase === 'PHASE_2' ? 'P2' : 'Done'
    additionalInfo = ` (${phaseLabel})`
  }

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${config.className}`}
    >
      <span>{config.icon}</span>
      <span>{config.label}{additionalInfo}</span>
    </span>
  )
}

export default CampaignTypeBadge
